// コマンドのパラメータで繰り返し使う JSON Schema。
//
// エージェント (MCP) は bpe_list_commands が返すスキーマだけを頼りにパラメータを組み立てるので、
// `{type:'object'}` のままだと中身を推測するしかない。受け付ける形をここに書き下して、
// 誤りは実行前に invalid-params で返す (描画結果を見るまで気づけない状態を避ける)。

import { BLEND_MODES } from '@/types/document'
import type { JsonSchema } from './registry'

/** 色は #rgb / #rrggbb のみ (color/convert.ts の hexToRgb と同じ)。8 桁のアルファ付きは未対応 */
export const HEX_PATTERN = '^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$'
export const hexSchema: JsonSchema = { type: 'string', pattern: HEX_PATTERN, description: '#rgb or #rrggbb' }
export const hexOrNullSchema: JsonSchema = { oneOf: [hexSchema, { type: 'null' }] }

const unit = (description?: string): JsonSchema => ({ type: 'number', minimum: 0, maximum: 1, description })
/** 効果は enabled: true でなければ描かれない (既定 false) */
const enabled: JsonSchema = { type: 'boolean', description: 'must be true for the effect to be drawn (default false)' }
const angle: JsonSchema = { type: 'number', minimum: -360, maximum: 360 }
const blendMode: JsonSchema = { type: 'string', enum: [...BLEND_MODES] }

/** グラデーションの色止め。位置のキーは offset ではなく pos */
const gradientStop: JsonSchema = {
  type: 'object',
  properties: { pos: unit('position along the gradient (not "offset")'), hex: hexSchema, alpha: unit() },
  required: ['pos', 'hex'],
  additionalProperties: false
}

/** 塗りつぶしレイヤー / 塗り (model/validate.ts validateFill) */
export const fillSchema: JsonSchema = {
  oneOf: [
    { type: 'object', properties: { kind: { type: 'string', enum: ['solid'] }, hex: hexSchema }, required: ['kind', 'hex'], additionalProperties: false },
    {
      type: 'object',
      properties: {
        kind: { type: 'string', enum: ['gradient'] },
        style: { type: 'string', enum: ['linear', 'radial', 'angle', 'reflected', 'diamond'] },
        stops: { type: 'array', items: gradientStop, description: 'at least one stop' },
        angle,
        scale: { type: 'number', minimum: 0.01, maximum: 10 },
        reverse: { type: 'boolean' }
      },
      required: ['kind', 'style', 'stops'],
      additionalProperties: false
    },
    {
      type: 'object',
      properties: {
        kind: { type: 'string', enum: ['pattern'] },
        patternId: { type: 'string', description: 'id from pattern.define / document.describe' },
        scale: { type: 'number', minimum: 0.05, maximum: 10 },
        offset: { type: 'object', properties: { x: { type: 'number' }, y: { type: 'number' } }, additionalProperties: false }
      },
      required: ['kind', 'patternId'],
      additionalProperties: false
    }
  ]
}

const point: JsonSchema = { type: 'object', properties: { x: { type: 'number' }, y: { type: 'number' } }, required: ['x', 'y'], additionalProperties: false }

/** シェイプの形状 (model/validate.ts validateGeometry) */
export const geometrySchema: JsonSchema = {
  oneOf: [
    { type: 'object', properties: { type: { type: 'string', enum: ['rect'] }, x: { type: 'number' }, y: { type: 'number' }, w: { type: 'number', minimum: 0 }, h: { type: 'number', minimum: 0 }, radius: { type: 'number', minimum: 0, maximum: 100000 } }, required: ['type', 'x', 'y', 'w', 'h'], additionalProperties: false },
    { type: 'object', properties: { type: { type: 'string', enum: ['ellipse'] }, x: { type: 'number' }, y: { type: 'number' }, w: { type: 'number', minimum: 0 }, h: { type: 'number', minimum: 0 } }, required: ['type', 'x', 'y', 'w', 'h'], additionalProperties: false },
    { type: 'object', properties: { type: { type: 'string', enum: ['polygon'] }, x: { type: 'number' }, y: { type: 'number' }, w: { type: 'number', minimum: 0 }, h: { type: 'number', minimum: 0 }, sides: { type: 'number', minimum: 3, maximum: 100 }, star: { type: 'number', minimum: 0, maximum: 0.99, description: '0 = polygon, >0 = star' } }, required: ['type', 'x', 'y', 'w', 'h'], additionalProperties: false },
    { type: 'object', properties: { type: { type: 'string', enum: ['line'] }, x1: { type: 'number' }, y1: { type: 'number' }, x2: { type: 'number' }, y2: { type: 'number' } }, required: ['type', 'x1', 'y1', 'x2', 'y2'], additionalProperties: false },
    {
      type: 'object',
      properties: {
        type: { type: 'string', enum: ['path'] },
        points: { type: 'array', items: { type: 'object', properties: { x: { type: 'number' }, y: { type: 'number' }, in: point, out: point }, required: ['x', 'y'], additionalProperties: false }, description: 'in / out are absolute bezier control points' },
        closed: { type: 'boolean' }
      },
      required: ['type', 'points'],
      additionalProperties: false
    }
  ]
}

const shadowFx = (): JsonSchema => ({
  type: 'object',
  properties: { enabled, blendMode, hex: hexSchema, opacity: unit(), angle, distance: { type: 'number', minimum: 0, maximum: 3000 }, spread: { type: 'number', minimum: 0, maximum: 100 }, size: { type: 'number', minimum: 0, maximum: 500 } },
  additionalProperties: false
})

const glowFx = (): JsonSchema => ({
  type: 'object',
  properties: { enabled, blendMode, hex: hexSchema, opacity: unit(), spread: { type: 'number', minimum: 0, maximum: 100 }, size: { type: 'number', minimum: 0, maximum: 500 }, source: { type: 'string', enum: ['center', 'edge'] } },
  additionalProperties: false
})

const overlayFx = (): JsonSchema => ({
  type: 'object',
  properties: { enabled, blendMode, opacity: unit(), hex: hexSchema, stops: { type: 'array', items: { type: 'object', properties: { pos: unit(), hex: hexSchema }, required: ['pos', 'hex'], additionalProperties: false }, description: 'gradientOverlay only' }, angle, style: { type: 'string', enum: ['linear', 'radial'] } },
  additionalProperties: false
})

/** レイヤースタイル 10 種 (model/validate.ts validateStyles)。省略した効果は付かない */
export const stylesSchema: JsonSchema = {
  type: 'object',
  properties: {
    dropShadow: shadowFx(),
    innerShadow: shadowFx(),
    outerGlow: glowFx(),
    innerGlow: glowFx(),
    bevel: {
      type: 'object',
      properties: { enabled, style: { type: 'string', enum: ['inner', 'outer', 'emboss'] }, depth: { type: 'number', minimum: 1, maximum: 1000 }, size: { type: 'number', minimum: 0, maximum: 500 }, soften: { type: 'number', minimum: 0, maximum: 100 }, angle, altitude: { type: 'number', minimum: 0, maximum: 90 }, highlightHex: hexSchema, highlightOpacity: unit(), shadowHex: hexSchema, shadowOpacity: unit() },
      additionalProperties: false
    },
    satin: {
      type: 'object',
      properties: { enabled, blendMode, hex: hexSchema, opacity: unit(), angle, distance: { type: 'number', minimum: 0, maximum: 500 }, size: { type: 'number', minimum: 0, maximum: 500 }, invert: { type: 'boolean' } },
      additionalProperties: false
    },
    colorOverlay: overlayFx(),
    gradientOverlay: overlayFx(),
    patternOverlay: {
      type: 'object',
      properties: { enabled, blendMode, opacity: unit(), patternId: { type: 'string' }, scale: { type: 'number', minimum: 0.05, maximum: 10 } },
      additionalProperties: false
    },
    stroke: {
      type: 'object',
      properties: { enabled, size: { type: 'number', minimum: 0, maximum: 500 }, position: { type: 'string', enum: ['inside', 'center', 'outside'] }, blendMode, opacity: unit(), hex: hexSchema },
      additionalProperties: false
    }
  },
  additionalProperties: false
}
